/* eslint-disable no-undef */
import mixpanel from "mixpanel-browser";

let __hasBeenInitialized = false;

const domLoadedPromise = new Promise((resolve) => {
  document.addEventListener("DOMContentLoaded", resolve);
});

async function initIfNeeded() {
  if (__hasBeenInitialized) {
    return true;
  }

  await domLoadedPromise;

  const element = document.getElementById("__mixpanel_key");
  if (element) {
    mixpanel.init(
      element.value,
      {
        record_sessions_percent: element.dataset.recordSessionsPercent || 1,
        record_mask_text_selector: '', //unmask all text elements
        record_block_selector: '' //unmask images and videos
      }
    );
    mixpanel.register(platformProperties());
    __hasBeenInitialized = true;
    return true;
  } else {
    return false;
  }
}

/**
 * Super properties registered on every web event so native-app (Hotwire Native
 * WebView) traffic can be told apart from mobile web. No PII.
 */
export function platformProperties(
  ua = window.navigator.userAgent,
  hotwireAttr = document.documentElement.hasAttribute("data-hotwire-native")
) {
  const app_platform = ua.startsWith("Supercast-iOS/") ? "ios" : ua.startsWith("Supercast-Android/") ? "android" : "web";
  return { hotwire_native: hotwireAttr || app_platform !== "web", app_platform };
}

/**
 * Logs an event to analytics. Automatically injects the current subdomain and path
 *
 * @param {String} eventName - The name of the event to log
 * @param {Object} eventProperties - An object containing the properties of the event
 */
export async function logEvent(eventName, eventProperties = {}) {
  if (!await initIfNeeded()) { return; }

  const { subdomain, path } = getSubdomainAndPath();

  mixpanel.track(eventName, {
    ...eventProperties,
    subdomain,
    path
  });

  maybeConsoleLog(eventName, eventProperties);
}

export async function setUserProperties(userProperties) {
  if (!await initIfNeeded()) { return; }

  mixpanel.people.set(userProperties);

  maybeConsoleLog('Set user properties', userProperties);
}

// Mixpanel only ever receives the user id. `contact` ({ email, name }) is
// forwarded to Beacon/Intercom (internal support tooling) and never to Mixpanel.
export async function identify(id, contact = {}) {
  if (!await initIfNeeded()) { return; }

  mixpanel.identify(id);

  if (window.Beacon) {
    window.Beacon("identify", {
      email: contact.email,
      name: contact.name,
      "admin-link": "https://app.supercast.com/admin/users/" + id
    });
  }

  const controller = window.Stimulus.getControllerForElementAndIdentifier(document.body, 'bridge--intercom');
  if (controller) {
    controller.identify({ userId: id, email: contact.email, name: contact.name });
  }

  maybeConsoleLog('Identify', { id });
}

export async function startSessionRecording() {
  if (!await initIfNeeded()) { return; }

  mixpanel.start_session_recording();

  maybeConsoleLog('Starting session recording');
}

/**
 * If the TLD of the the current domain is .test (i.e. we're in dev), log to the console
 */
export function maybeConsoleLog(eventName, eventProperties) {
  if (window.location.hostname.endsWith('.test')) {
    console.log(`Analytics event: ${eventName}`, eventProperties);
  }
}

/**
 * Extracts the current subdomain and path from window.location
 */
function getSubdomainAndPath() {
  const { hostname } = window.location;
  const [subdomain, ...path] = hostname.split('.');

  return {
    subdomain,
    path: path.join('.')
  };
}
